/**
 * Single classification rule for technical spec sheets across the platform.
 *
 * Replaces the three copies of `Math.floor(Number(model)) > 100` that lived in
 * web-api (documents.controller.ts, twice) and in the eproject frontend
 * (ArticleCollectionApi.ts).
 */

export type SpecSheetVariant = 'model' | 'umbrella' | 'reference'

/** Boundary owned by the CMS writer, not by us. See `staticSpecSheetCanExist`. */
const FIRST_MODEL_WITHOUT_STATIC = 100

/**
 * Whether a pre-uploaded static spec sheet can exist in the CMS for this model.
 *
 * Source of truth is the WRITER: `cms/tags/Tags.ts` and the FTP sync
 * (`vibiasync/cms/src/sync_ftp/create_object_trigger.js`) refuse to ingest the file
 * when `parseInt(model) >= 100`. Anything else may have one.
 *
 * Careful — alphanumeric models DO have statics: the Plusminus Essentials Toolbox
 * sheets are TB01..TB04 in five languages. Phrasing this rule as "integer below 100"
 * would silently exclude them.
 */
export const staticSpecSheetCanExist = (
    model: string | number | null | undefined,
): model is string | number => {
    if (model === null || model === undefined) return false

    const raw = String(model).trim()
    if (raw === '') return false

    const parsed = Number(raw)
    const isExcludedByWriter = Number.isInteger(parsed) && parsed >= FIRST_MODEL_WITHOUT_STATIC

    return !isExcludedByWriter
}

export interface SpecSheetSubject {
    model?: string | number | null
    /** Mongo stores this as 0/1, the frontend sends a boolean. Both mean the same. */
    isPersonalized?: boolean | number | null
    /**
     * Present even on unconfigured products, where it equals the model (`TB01`).
     * Deliberately NOT used to decide: only `isPersonalized` marks a configured item.
     */
    reference?: string | null
}

export const classifySpecSheet = (subject: SpecSheetSubject): SpecSheetVariant => {
    if (subject.isPersonalized) return 'reference'
    if (staticSpecSheetCanExist(subject.model)) return 'umbrella'

    return 'model'
}

export interface ArticleIdentity {
    model?: string | number | null
    /** Projects store it as `referenceCode`; some payloads use `reference`. */
    reference?: string | null
    referenceCode?: string | null
}

/**
 * Whether an article inside a composition has actually been configured.
 *
 * Mirrors the domain rule in `catalog/Article.ts` — `reference != model` — which is
 * also what `CreationBOM` uses to mark a creation as personalised only when every one
 * of its articles is. An unconfigured article carries its model as reference (or none
 * at all), so asking for "the sheet of that reference" fails: it is not a sellable
 * product. Those must be requested by model instead (DV-6520).
 */
export const isArticleConfigured = (article: ArticleIdentity): boolean => {
    const reference = article.referenceCode ?? article.reference
    if (!reference) return false

    return String(reference) !== String(article.model ?? '')
}
